'use client';

import { motion, Variants } from 'motion/react';
import { useEffect, useState } from 'react';

interface LighthouseScoreAnimatedGaugeProps {
  percentage: number;
  color: string;
  label?: string;
  isSuccess: boolean;
  isPending: boolean;
  isError: boolean;
}

export const LighthouseScoreAnimatedGauge = ({
  color,
  label,
  percentage,
  isSuccess,
  isPending,
  isError,
}: LighthouseScoreAnimatedGaugeProps) => {
  const LOADING_STATE_FILL = '#b4b9c0';

  const [prevColor, setPrevColor] = useState(LOADING_STATE_FILL);
  const [prevPercentage, setPrevPercentage] = useState(percentage);

  useEffect(() => {
    if (isSuccess) {
      setPrevColor(color);
      setPrevPercentage(percentage);
    }
  }, [color, percentage, isSuccess]);

  useEffect(() => {
    if (isError) {
      setPrevColor(LOADING_STATE_FILL);
      setPrevPercentage(0);
    }
  }, [isError]);

  const width = 256;
  const height = width / 2;
  const strokeWidth = 24;
  const radius = height - strokeWidth / 2;
  const circumference = radius * Math.PI;

  const variants: Variants = {
    loading: () => ({
      strokeDashoffset: [circumference, 0, circumference],
      transition: {
        duration: 3,
        ease: 'easeInOut',
        repeat: Infinity,
      },
    }),
    final: (custom: { color: string; percentage: number }) => ({
      strokeDashoffset: circumference - (custom.percentage / 100) * circumference,
      stroke: custom.color,
      transition: { duration: 0.5, ease: 'easeInOut' },
    }),
  };

  return (
    <div className="mx-auto w-full max-w-[256px]">
      <div className="relative w-full">
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <path
            d={`M ${strokeWidth / 2},${height} A ${radius},${radius} 0 0 1 ${width - strokeWidth / 2},${height}`}
            fill="none"
            stroke="#E2E8F0"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <motion.path
            d={`M ${strokeWidth / 2},${height} A ${radius},${radius} 0 0 1 ${width - strokeWidth / 2},${height}`}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            variants={variants}
            initial={isPending ? 'loading' : 'final'}
            animate={isPending ? 'loading' : 'final'}
            custom={{
              color: isPending ? LOADING_STATE_FILL : prevColor,
              percentage: prevPercentage,
            }}
            stroke={isPending ? LOADING_STATE_FILL : prevColor}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-end">
          {isSuccess && (
            <span className="text-xs font-medium text-gray-500 xl:text-sm">{label}</span>
          )}
          {isSuccess ? (
            <span className="text-4xl font-bold text-gray-900 xl:text-5xl">{percentage}%</span>
          ) : (
            <span className="text-4xl font-bold text-gray-900 xl:text-5xl">Check</span>
          )}
        </div>
      </div>
    </div>
  );
};
